import { LABEL_MIN_PX, TICK_MAX_PX, type Placed } from "../geometry";
import { type Bands, theme } from "../theme";
import { drawEdges, drawHandles, showLook, trackLook } from "./runs";
import { CAPTION, FACE, captionLines, writeCaption } from "./text";
import type { DrawInput } from "./types";
import type { TimelineBlock } from "../../api/types";

export function drawShow(
  ctx: CanvasRenderingContext2D,
  placed: Placed<TimelineBlock>,
  b: Bands,
  input: DrawInput,
  startEdge: boolean,
  endEdge: boolean,
): void {
  const { item } = placed;
  // One reading of what this block is, shared with the seam test: a boundary is only dropped
  // between two blocks wearing the same colours, so the two must not disagree about which
  // colours those are.
  const look = showLook(item);
  const live = look === "live";
  const provisional = look === "provisional";
  const dead = look === "dead";
  const hovered = item.id !== null && item.id === input.hoverId;
  const selected = item.id !== null && item.id === input.selectedId;

  const x0 = Math.max(-2, placed.x0);
  const x1 = Math.min(input.view.width + 2, placed.x1);
  const top = b.showTop;
  const height = b.showBottom - top;

  // The fill is `fillRuns`' business: a run of blocks with no boundary drawn between them is
  // one region, and painting it a block at a time leaves a line down every seam.

  const edge = dead
    ? theme.tombstoneEdge
    : live
      ? theme.liveEdge
      : provisional
        ? theme.provisionalEdge
        : theme.showEdge;

  // Hard start and end edges. The fill is translucent so blocks read as a layer over the
  // timeline, but the boundaries are the information: they are where the show begins and
  // ends, and they are drawn opaque.
  // Dashed for both: a live block's end is where the recording currently reaches, and a
  // provisional one's is where detection currently thinks it is. Neither is settled.
  drawEdges(ctx, placed.x0, placed.x1, top, height, edge, provisional || live, startEdge, endEdge);

  if (selected) {
    ctx.strokeStyle = theme.selection;
    ctx.lineWidth = 2;
    ctx.strokeRect(x0 + 1, top + 1, x1 - x0 - 2, height - 2);
  }

  // Grab handles, drawn only for the block under the pointer or the selected one: showing
  // them on every block at once turns the timeline into a row of buttons.
  //
  // No end handle while the show is on air. That edge is drawn at the clock rather than at the
  // stored end, and the segmenter owns it: any edit makes the stored span differ from the
  // detected one, which is the exact test worker.segmenter._is_untouched uses to decide to stop
  // extending a row. Offering the handle would invite an editor to grab what looks like the live
  // edge and silently freeze the show that is on air.
  if (input.editable && (hovered || selected) && item.state !== "exported") {
    drawHandles(ctx, live ? [placed.x0] : [placed.x0, placed.x1], top, height, edge);
  }

  const width = x1 - x0;
  if (width < LABEL_MIN_PX) return;

  // The tag joins the same stack rather than being placed against the middle itself: it is
  // one more line of the caption, and a block that carries one would otherwise sit its title
  // higher than its neighbours' do.
  const lines = captionLines(item, look, CAPTION.show.face, input.t("draw.untitled"), input.t);
  const tag = live ? input.t("draw.tagLive") : provisional ? input.t("draw.tagUnverified") : null;
  if (tag) {
    lines.push({ text: tag, colour: live ? theme.liveEdge : theme.provisionalEdge, ...FACE.small });
  }
  writeCaption(ctx, lines, "show", x0, width, top, height);
}

export function drawTrack(
  ctx: CanvasRenderingContext2D,
  placed: Placed<TimelineBlock>,
  b: Bands,
  input: DrawInput,
  startEdge: boolean,
  endEdge: boolean,
  /**
   * The width at which a track becomes a block in its own right: wide enough to carry its name,
   * and so wide enough to be worth delimiting. `Infinity` in a merged lane, where no track is.
   */
  nameMinPx: number,
): void {
  const { item } = placed;
  const look = trackLook(item);

  const selected = item.id !== null && item.id === input.selectedId;

  const top = b.trackTop;
  const height = b.trackBottom - top;
  const x0 = Math.max(-2, placed.x0);
  const x1 = Math.min(input.view.width + 2, placed.x1);

  // No border on a matched track, for the reason it has no wash: a line at each end is still
  // the show cut into pieces, which is the one thing the record inside it is not. Its
  // neighbour in the lane draws the boundary between them -- see `seam`.
  if (look !== "matched") {
    drawEdges(
      ctx,
      placed.x0,
      placed.x1,
      top,
      height,
      look === "dead" ? theme.tombstoneEdge : theme.trackEdge,
      false,
      startEdge,
      endEdge,
    );
  }

  if (item.starred) {
    ctx.fillStyle = theme.trackStarred;
    ctx.fillRect(x0, top, Math.min(3, x1 - x0), height);
  }
  if (selected) {
    ctx.strokeStyle = theme.selection;
    ctx.lineWidth = 2;
    ctx.strokeRect(x0 + 1, top + 1, x1 - x0 - 2, height - 2);
  }

  const width = x1 - x0;
  if (width < nameMinPx) return;
  writeCaption(
    ctx,
    captionLines(
      item,
      look === "dead" ? "dead" : "track",
      CAPTION.track.face,
      input.t("draw.unknown"),
      input.t,
    ),
    "track",
    x0,
    width,
    top,
    height,
  );
}

/**
 * A track that is playing but has not been segmented yet.
 *
 * Deliberately not drawn as a normal track. It is a claim from the title log rather than a row
 * anybody could open, so it borrows the live colours and stays translucent: it should read as
 * the leading edge of the timeline catching up, and it must not be mistaken for a block that
 * has been recorded and named.
 */
export function drawGhost(
  ctx: CanvasRenderingContext2D,
  placed: Placed<TimelineBlock>,
  b: Bands,
  input: DrawInput,
): void {
  const top = b.trackTop;
  const height = b.trackBottom - top;
  const x0 = Math.max(-2, placed.x0);
  const x1 = Math.min(input.view.width + 2, placed.x1);

  ctx.save();
  ctx.globalAlpha = 0.7;
  ctx.fillStyle = theme.liveFill;
  ctx.fillRect(x0, top, x1 - x0, height);
  drawEdges(ctx, placed.x0, placed.x1, top, height, theme.liveEdge, true);

  const width = x1 - x0;
  if (width >= LABEL_MIN_PX) {
    // Same stack as a settled track -- title over artist -- because `ghostTrack` already split
    // the raw title the way the worker will. Only the ink differs, so the block reads as live.
    writeCaption(
      ctx,
      captionLines(placed.item, "live", CAPTION.track.face, input.t("draw.unknown"), input.t),
      "track",
      x0,
      width,
      top,
      height,
    );
  }
  ctx.restore();
}

export function drawTick(
  ctx: CanvasRenderingContext2D,
  placed: Placed<TimelineBlock>,
  b: Bands,
  input: DrawInput,
): void {
  const hovered = placed.item.id !== null && placed.item.id === input.hoverId;
  // Full height, like every other block: a tick is a track too narrow to label, not a
  // lesser kind of thing, and at seven-day zoom the ticks are the only content there is.
  const bottom = b.trackBottom;
  const top = b.trackTop;
  const x = Math.round(placed.x0) + 0.5;
  const width = Math.max(1, Math.min(placed.x1 - placed.x0, TICK_MAX_PX));

  ctx.fillStyle = placed.item.starred
    ? theme.trackStarred
    : !placed.item.playable
      ? theme.tombstoneEdge
      : hovered
        ? theme.trackEdge
        : theme.trackFillHover;
  ctx.fillRect(x, top, width, bottom - top);
}
